/**
 * AceitarConvitePage — primeiro acesso via token de convite (B1.5 spec §2.3).
 *
 * URL: /cliente/aceitar-convite/:token
 *
 * Fluxo:
 *   1. Mount → não valida token (deixa pro POST descobrir)
 *   2. User preenche senha + confirmar (+ nome opcional)
 *   3. Submit → POST /api/v1/cliente/aceitar-convite/:token
 *   4. 404 → token-invalido-ou-expirado (mostra mensagem clara)
 *   5. 409 → email-pertence-a-user-interno (mostra mensagem)
 *   6. 400 → senha fraca / payload invalid (mostra detalhe)
 *   7. 200 → backend retornou + Set-Cookie auto-login + user/clientes
 *      → refreshSession do context + navigate /cliente/dashboard
 *
 * Validações client-side mínimas (server-side é a fonte da verdade):
 *   - senha === confirmacao
 *   - senha >= 8 chars (espelha backend validatePasswordPolicy)
 *
 * NÃO REVERTER:
 *   - Auto-login pós-aceite (UX — não pede pra logar de novo)
 *   - Validação client-side mínima — backend valida tudo de novo
 *   - Token na URL — vem do email de convite (clicável)
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, Button, Input, Label } from '@/components/ui';
import { useClienteAuth } from '@/contexts/ClienteAuthContext';
import { clienteApi, ClienteApiError } from '@/lib/cliente-api';
import { mensagemErro } from '@/lib/mensagem-publica';

export function AceitarConvitePage() {
  const { token } = useParams<{ token: string }>();
  const { refreshSession } = useClienteAuth();
  const navigate = useNavigate();
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (!token) {
      setErrorMsg('Link inválido. Peça à IGM um novo convite.');
      return;
    }
    if (senha.length < 8) {
      setErrorMsg('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (senha !== confirmacao) {
      setErrorMsg('As duas senhas digitadas não são iguais.');
      return;
    }
    setLoading(true);
    try {
      await clienteApi.post(`/aceitar-convite/${encodeURIComponent(token)}`, {
        senha,
        nome: nome.trim() || undefined,
      });
      // Backend setou cookie auto-login. Atualiza contexto + redirect.
      await refreshSession();
      navigate('/cliente/dashboard');
    } catch (err) {
      if (err instanceof ClienteApiError && err.status === 404) {
        setErrorMsg('Este convite já foi usado ou expirou. Peça um novo à IGM.');
      } else if (err instanceof ClienteApiError && err.status === 409) {
        setErrorMsg('Este e-mail já é usado por alguém da equipe IGM. Peça à IGM para usar outro.');
      } else if (err instanceof ClienteApiError && err.status === 400) {
        setErrorMsg('Essa senha é fácil demais. Use ao menos 8 caracteres com letras e números.');
      } else {
        setErrorMsg(mensagemErro(err, 'Não conseguimos criar sua conta agora. Tente de novo.'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-md pt-8" data-testid="cliente-aceitar-convite-page">
      <div className="rounded-2xl border border-line bg-paper-surface p-6 shadow-card dark:border-line-dark dark:bg-paper-dark-surface md:p-8">
        <h1 className="mb-2 font-display text-3xl tracking-tight text-ink-1 dark:text-ink-dark-1">
          Boas-vindas ao Portal IGM
        </h1>
        <p className="mb-6 text-sm text-ink-2 dark:text-ink-dark-2">
          Falta só criar sua senha. Depois disso você já entra e acompanha tudo do seu condomínio.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="nome">Como podemos te chamar? (opcional)</Label>
            <Input
              id="nome"
              autoComplete="name"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex.: Ana Souza"
              disabled={loading}
              data-testid="aceitar-nome-input"
            />
          </div>
          <div>
            <Label htmlFor="senha" required>
              Crie uma senha
            </Label>
            <Input
              id="senha"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              required
              minLength={8}
              disabled={loading}
              data-testid="aceitar-senha-input"
            />
            <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
              Pelo menos 8 caracteres, com letras e números.
            </p>
          </div>
          <div>
            <Label htmlFor="confirmacao" required>
              Repita a senha
            </Label>
            <Input
              id="confirmacao"
              type="password"
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              required
              disabled={loading}
              data-testid="aceitar-confirmacao-input"
            />
          </div>

          {errorMsg && (
            <Alert variant="danger" data-testid="aceitar-error">
              {errorMsg}
            </Alert>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="w-full"
            data-testid="aceitar-submit-btn"
          >
            {loading ? 'Criando seu acesso…' : 'Criar senha e entrar'}
          </Button>
        </form>
      </div>
    </div>
  );
}
